import { Locale, enUS, es as esES, ptBR } from 'date-fns/locale';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { CookiesKeys } from '../../constants/cookiesKeys';
import { Languages, LanguagesValuesType, LanguagesWithoutRegion } from '../../constants/languages';
import getCookieValue from '../../utils/getCookieValue';
import { default as setCookieValues } from '../../utils/setCookieValue';
import en from './i18n/dictionaries/en.json';
import es from './i18n/dictionaries/es.json';
import pt from './i18n/dictionaries/ptBR.json';

type CurrentLocaleProps = {
  locale: string;
}

type InterpolationParams = Record<string, string | number>;

export type i18nContextProps = {
  updateLocale: (newLocale: LanguagesValuesType) => void;
  translate: (keys: string[], params?: InterpolationParams) => string;
  supportedLocales: ({ lang }: {lang: string}) => Locale;
  currentLocale: CurrentLocaleProps;
};

const I18nContext = createContext({} as i18nContextProps);

/**
 * Initializes the i18n configuration for language detection and internationalization support.
 * @function
 * @returns {void}
 */
i18n.use(LanguageDetector).use(initReactI18next).init({
  debug : false,

  /**
   * Language detection configuration.
   * @type {object}
   * @property {string[]} order - Order in which to detect the language.
   * @property {string[]} caches - Where to store the detected language.
   * @property {string} lookupCookie - Name of the cookie to look up the language.
  */
  detection : { order : ['cookie'], caches : ['cookie'] },

  /**
   * Fallback language if the detected language is not available.
   * @type {string}
  */
  fallbackLng : Languages.pt_br,

  /**
   * Interpolation configuration.
   * @type {object}
   * @property {boolean} escapeValue - Whether to escape values.
  */
  interpolation : { escapeValue : false },

  /**
   * Resource bundles for each supported language.
   * @type {object}
   * @property {object} en - English translations.
   * @property {object} pt - Portuguese translations.
   * @property {object} es - Spanish translations.
  */
  resources : {
    en : { translation : { ...en } },
    pt : { translation : { ...pt } },
    es : { translation : { ...es } }
  }
});

function I18nProvider({ children }: { children: ReactNode }) {
  const [currentLocale, setCurrentLocale] = useState<CurrentLocaleProps>(() => {
    const cookieLocale = getCookieValue({ cookieKey : CookiesKeys.LOCALE, needPrefix : false });
    return cookieLocale ? { locale : cookieLocale } : { locale : Languages.pt_br };
  });

  const { t } = useTranslation();
  const [queryParam] = useSearchParams();

  /**
   * Returns the supported locale configuration based on the given language code.
   * @param lang - The language code to get the locale for.
   * @returns The locale configuration object for the specified language code.
  */
  const supportedLocales = ({ lang }: {lang: string}) => {
    switch (lang) {
    case 'pt-BR': return ptBR;
    case 'es-ES': return esES;
    case 'en-US': return enUS;
    default: return ptBR;
    }
  };

  /**
   * Sets a cookie with the given locale value.
   * @param locale - The locale string to set as the cookie value.
   * @returns void
  */
  const setLocaleCookie = (locale: string): void => {
    setCookieValues([{ key : CookiesKeys.LOCALE, value : locale }]);
  };

  /**
   * Formats a locale string by replacing the underscore with a dash and converting the suffix to uppercase.
   * @param locale - The locale string to format (e.g., "en_us").
   * @returns The formatted locale string (e.g., "en-US").
  */
  const formatLocale = (locale: string): string => {
    const [prefix, suffix] = locale.split('_');
    return `${prefix}-${suffix.toUpperCase()}`;
  };

  /**
   * Translates a given array of keys into a localized string.
   * Joins the keys into a dot-separated path and uses the translation function `t` to get the localized string.
   * If the translation is not found, logs a warning and returns a default message.
   * @param keys - An array of strings representing the path to the translation key.
   * @returns The localized string or a default message if the translation is not found.
  */
  const translate = (keys: string[], params?: InterpolationParams) => {
    const path = keys.join('.');
    const translated = t(path, params);

    if (translated === path) {
      console.warn(`Translation not found for path: ${path}`);
      return 'Translation not found';
    }

    return translated;
  };

  /**
   * Updates the application's locale.
   * Changes the language in the i18n instance, formats the locale, sets a cookie with the new locale value,
   * and updates the current locale state.
   * @param newLocale - The new locale string to be set (e.g., "en_us").
  */
  const updateLocale = (newLocale: LanguagesValuesType) => {
    i18n.changeLanguage(newLocale);
    const locale = formatLocale(newLocale);
    setLocaleCookie(locale);
    setCurrentLocale({ locale });
  };

  /**
   * useEffect hook to initialize the locale settings for the application.
   * This hook runs on component mount and performs the following steps:
   * - Retrieves the locale from the cookies, if available.
   * - Sets the current locale state and updates the i18n language if a locale is found in cookies.
   * - If no locale is found in cookies, it checks the user's browser settings for a matching locale.
   * - Sets the browser's locale in cookies and updates the i18n language.
   * - If no matching locale is found in the browser settings, it defaults to 'pt-BR'.
   * - Sets the default locale in cookies and updates the i18n language.
  */
  useEffect(() => {
    const initializeLocale = (): void => {
      const cookieLocale = getCookieValue({ cookieKey : CookiesKeys.LOCALE, needPrefix : false });

      if (cookieLocale) {
        const locale = cookieLocale.replace('-', '_').toLowerCase();
        updateLocale(locale);
        return;
      }

      const localeParam = queryParam.get('locale');
      if (localeParam) {
        const formattedLocaleParam = localeParam.replace('-', '_').toLowerCase();
        if (Object.keys(Languages).includes(formattedLocaleParam)) {
          updateLocale(formattedLocaleParam);
          return;
        }
      }

      const foundLanguage = navigator.languages
        .map((l) => l.substring(0, 2).toLowerCase())
        .find((l) => l in LanguagesWithoutRegion) as keyof typeof LanguagesWithoutRegion | undefined;

      const userLanguage = foundLanguage ? LanguagesWithoutRegion[foundLanguage] : Languages.pt_br;
      updateLocale(userLanguage);
    };

    initializeLocale();
  }, []);

  const shared = useMemo(() => ({
    updateLocale, translate, supportedLocales, currentLocale
  }), [
    updateLocale, translate, supportedLocales, currentLocale
  ]);

  return (
    <I18nContext.Provider value={shared}>
      {children}
    </I18nContext.Provider>
  );
}

I18nProvider.displayName = 'I18nProvider';
export const useI18nContext = (): i18nContextProps => useContext(I18nContext);

export { I18nContext, I18nProvider };

